import React from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X } from 'lucide-react';

/**
 * Generic Delete Confirmation Modal
 * Reusable across all entities (Products, Customers, Orders, Staff, etc.)
 */
const DeleteConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    entityName = 'item',
    itemName,
    isDeleting = false
}) => {
    if (!isOpen) return null;
    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-[400px] overflow-hidden animate-scale-in">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-5 py-3 relative">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-all disabled:opacity-50"
                    >
                        <X size={16} />
                    </button>
                    <h2 className="text-lg font-bold pr-8">Delete {entityName}</h2>
                    <p className="text-red-100 text-xs mt-0.5">This action cannot be undone</p>
                </div>

                {/* Content */}
                <div className="p-5">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <Trash2 size={20} className="text-red-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-800 font-semibold mb-1">
                                Are you sure you want to delete this {entityName.toLowerCase()}?
                            </p>
                            {itemName && (
                                <p className="text-xs text-gray-600">
                                    <span className="font-semibold">{itemName}</span> will be permanently removed.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={onClose}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all font-semibold disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all font-semibold shadow-md hover:shadow-lg flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {isDeleting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} />
                                    Delete {entityName}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DeleteConfirmModal;
